import { ConditionalFunctionString } from '../../conditions.types'
import type { SemanticType } from '../../../semanticTypes'
import { durationToDays } from '../runtimeHelpers'
import { semanticTypeRuntimePath } from './utils'

/**
 * Configuration for the duration comparison factories. The
 * `startDateSemanticType` anchors `durationToDays` on the upstream value at
 * runtime so both sides of the comparison resolve against the same reference
 * date — the quote's start date when defined, or 2026-01-01 as a fallback.
 */
export type DurationFunctionStringConfig = {
  startDateSemanticType?: SemanticType
}

// Duration operators short-circuit to false when the property is null/undefined.
// This matches the contract that an empty/single-phase quote with no resolvable
// totalDuration shouldn't satisfy any duration comparison — including
// NOT_EQUALS — per the BADGE-5597 design.
const buildDurationComparison =
  (operator: '<' | '>' | '===' | '!==') =>
  ({
    startDateSemanticType
  }: DurationFunctionStringConfig = {}): ConditionalFunctionString =>
  ({ property, value }) => {
    const startDateArg = startDateSemanticType
      ? semanticTypeRuntimePath(startDateSemanticType)
      : 'undefined'
    return `${property} != null && ${durationToDays(property, startDateArg)} ${operator} ${durationToDays(JSON.stringify(value), startDateArg)}`
  }

export const durationLessThan = buildDurationComparison('<')
export const durationGreaterThan = buildDurationComparison('>')
export const durationEquals = buildDurationComparison('===')
export const durationNotEquals = buildDurationComparison('!==')
